import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { QrCode, Plus, RefreshCw, CheckCircle, XCircle, Link2, ExternalLink, AlertTriangle } from 'lucide-react';
import { SouscriptionSearch, TYPE_LABELS, DOC_TYPES } from './qrRegistryShared';
import QRCode from 'react-qr-code';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QRRegistryForm({ formData, setFormData, selectedSub, setSelectedSub, typeFilters, setTypeFilters, handleSubmit, handleResetForm, handleSelectSouscription, submitting, lastCreated, setLastCreated, headers }) {
  const [refStatus, setRefStatus] = useState(null); // {exists, target_label, ...}
  const [refChecking, setRefChecking] = useState(false);

  useEffect(() => {
    const ref = (formData.reference || '').trim();
    if (!ref) { setRefStatus(null); return; }
    setRefChecking(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/qr-codes/check/${encodeURIComponent(ref)}`, { headers });
        setRefStatus(res.data);
      } catch {
        setRefStatus(null);
      } finally {
        setRefChecking(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [formData.reference, headers]);

  return (
        <Card className="border-indigo-200 bg-indigo-50/30" data-testid="qr-registry-form">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="h-5 w-5 text-indigo-600" />
              Lier un QR Code a une souscription
            </CardTitle>
            <CardDescription>
              Saisissez la reference QR et selectionnez la souscription cible. Le QR deviendra immediatement verifiable.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {lastCreated ? (
              /* Success confirmation */
              <div className="space-y-4" data-testid="qr-registry-success">
                <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-emerald-800">QR Code lie et verifiable !</p>
                      <p className="text-sm text-emerald-600">La reference est maintenant reconnue par le systeme de verification.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm bg-white/60 rounded-lg p-3">
                    <div>
                      <span className="text-gray-500">Reference QR:</span>
                      <p className="font-mono font-medium text-indigo-700">{lastCreated.reference}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Souscription:</span>
                      <p className="font-medium">{lastCreated.souscription_info?.ref_solidaire || lastCreated.souscription_id}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Entreprise:</span>
                      <p className="font-medium">{lastCreated.souscription_info?.raison_sociale || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Statut liaison:</span>
                      <p className="font-medium">
                        {lastCreated.souscription_found
                          ? <span className="text-emerald-700">Souscription trouvee</span>
                          : <span className="text-amber-700">Souscription non trouvee</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`${BACKEND_URL}/api/verify-page/${lastCreated.document_type}/${lastCreated.reference}`, '_blank')}
                      className="border-emerald-300 text-emerald-700"
                      data-testid="qr-registry-test-page"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Tester la page de verification
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setFormData({ reference: '', target_type: 'souscription', target_id: '', souscription_id: '', document_type: 'rcds', description: '' });
                        setSelectedSub(null);
                        setLastCreated(null);
                      }}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="qr-registry-add-another"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Lier un autre QR
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleResetForm}>Fermer</Button>
                  </div>
                </div>
              </div>
            ) : (
              /* Creation form */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference QR Code *</label>
                    <input
                      type="text"
                      value={formData.reference}
                      onChange={e => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                      placeholder="Ex: COPPAM-RCDS-2025-ABC123"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      data-testid="qr-registry-input-reference"
                    />
                    <p className="text-xs text-gray-500 mt-1">La reference inscrite dans le QR code du document</p>
                    {formData.reference.trim() && (
                      <div className="mt-3 flex items-center gap-3 p-3 bg-white border border-indigo-200 rounded-lg" data-testid="qr-live-preview">
                        <div className="bg-white p-1.5 rounded border border-slate-200">
                          <QRCode
                            value={`${BACKEND_URL}/api/verify-page/${formData.document_type}/${formData.reference.trim()}`}
                            size={92}
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-indigo-700">Aperçu du QR code</p>
                          <p className="text-[11px] text-gray-500 break-all mt-1">
                            {`${BACKEND_URL}/api/verify-page/${formData.document_type}/${formData.reference.trim()}`}
                          </p>
                          <div className="mt-2" data-testid="qr-ref-status">
                            {refChecking && (
                              <span className="text-[11px] text-gray-400 flex items-center gap-1">
                                <RefreshCw className="w-3 h-3 animate-spin" /> Vérification…
                              </span>
                            )}
                            {!refChecking && refStatus?.exists && (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300 gap-1 text-[11px] font-semibold" data-testid="qr-ref-linked">
                                <AlertTriangle className="w-3 h-3" />
                                Déjà liée à {refStatus.target_label || 'une autre cible'}
                              </Badge>
                            )}
                            {!refChecking && refStatus && !refStatus.exists && (
                              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 gap-1 text-[11px] font-semibold" data-testid="qr-ref-available">
                                <CheckCircle className="w-3 h-3" />
                                Référence disponible
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <SouscriptionSearch
                    onSelect={handleSelectSouscription}
                    headers={headers}
                    selectedTypes={typeFilters}
                  />
                </div>

                {/* Filtres de type pour la recherche */}
                <div className="flex flex-wrap items-center gap-2 -mt-1">
                  <span className="text-xs text-gray-500 font-medium">Chercher dans :</span>
                  {Object.entries(TYPE_LABELS).map(([key, meta]) => {
                    const isActive = typeFilters.includes(key);
                    return (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setTypeFilters(prev =>
                          prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]
                        )}
                        className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                          isActive
                            ? `${meta.color} border-current font-semibold`
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                        data-testid={`qr-type-filter-${key}`}
                      >
                        {meta.icon} {meta.label}
                      </button>
                    );
                  })}
                </div>

                {/* Cible sélectionnée (multi-type) */}
                {selectedSub && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between" data-testid="qr-selected-target">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge className={`${(TYPE_LABELS[selectedSub.target_type] || TYPE_LABELS.souscription).color} text-[10px] uppercase font-semibold flex-shrink-0`}>
                        {(TYPE_LABELS[selectedSub.target_type] || TYPE_LABELS.souscription).icon} {(TYPE_LABELS[selectedSub.target_type] || TYPE_LABELS.souscription).label}
                      </Badge>
                      <div className="text-sm min-w-0">
                        <p className="font-medium text-gray-900 truncate">{selectedSub.label_main || selectedSub.ref_solidaire}</p>
                        <p className="text-gray-500 text-xs truncate">{selectedSub.label_sub || selectedSub.raison_sociale || 'N/A'}{selectedSub.statut ? ` — ${selectedSub.statut}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {selectedSub.has_qr && (
                        <Badge className="bg-blue-700 text-white text-xs font-semibold">
                          <QrCode className="h-3 w-3 mr-1" />
                          QR existant: {selectedSub.qr_reference}
                        </Badge>
                      )}
                      <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedSub(null); setFormData(prev => ({ ...prev, target_id: '', souscription_id: '' })); }}>
                        <XCircle className="h-4 w-4 text-gray-400" />
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de document</label>
                    <select
                      value={formData.document_type}
                      onChange={e => setFormData(prev => ({ ...prev, document_type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      data-testid="qr-registry-select-type"
                    >
                      {DOC_TYPES.map(dt => (
                        <option key={dt.value} value={dt.value}>{dt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel)</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Ex: QR ancien format, document legacy"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      data-testid="qr-registry-input-description"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-2">
                  {refStatus?.exists && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800" data-testid="qr-ref-alert">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        Cette référence est déjà liée à{' '}
                        <a
                          href={`${BACKEND_URL}/api/verify-page/${refStatus.document_type || formData.document_type}/${encodeURIComponent(refStatus.reference || formData.reference.trim())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold underline hover:text-amber-900"
                          data-testid="qr-ref-alert-link"
                        >
                          {refStatus.target_label || 'une autre cible'}
                        </a>
                        {refStatus.document_type ? ` (${TYPE_LABELS[refStatus.document_type] || refStatus.document_type})` : ''}. Modifiez la référence pour pouvoir enregistrer.
                      </span>
                    </div>
                  )}
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={handleResetForm}>Annuler</Button>
                    <Button
                      type="submit"
                      disabled={submitting || !formData.reference.trim() || !formData.target_id.trim() || refStatus?.exists}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="qr-registry-submit-btn"
                    >
                      {submitting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                      {refStatus?.exists ? 'Référence déjà utilisée' : 'Lier et rendre verifiable'}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

  );
}
